import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useSpring, useReducedMotion } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';
import MagneticButton from './MagneticButton';
import ClearWayExplainer, { EXPLAINER_DURATION, EXPLAINER_SCENES } from './explainer/ClearWayExplainer';
import ClearWayExplainerMobile from './explainer/ClearWayExplainerMobile';

/** Scroll distance (in viewport heights) that scrubs through the full story. */
const SCROLL_VH = 400;

/**
 * The "resting" point on the timeline shown before the user scrolls at all —
 * chosen so the intro's own fade-in (pill + wordmark + tagline) has already
 * settled, rather than greeting visitors with the animation's very first,
 * still-invisible frame.
 */
const REST_TIME = 3.5;

/**
 * Drives the explainer's timeline from scroll position instead of a clock.
 * `time` sits at REST_TIME (the intro, fully faded in) while the pinned
 * section is still below the fold, ramps linearly up to EXPLAINER_DURATION
 * across the section's scroll range, then holds at the end once scrolled
 * past — a scrubbable "video" rather than an autoplaying one.
 */
function useScrollDrivenTime(scrollRef, duration, restTime) {
  const { scrollYProgress } = useScroll({ target: scrollRef, offset: ['start start', 'end end'] });
  // Wheel/trackpad scrolling arrives in discrete jumps; a spring turns those
  // steps into a continuous scrub so the story glides between frames instead
  // of jump-cutting. restDelta 0.0004 of progress ≈ 0.02s of timeline.
  const smoothed = useSpring(scrollYProgress, { stiffness: 110, damping: 26, mass: 0.5, restDelta: 0.0004 });
  const [time, setTime] = useState(() => restTime + scrollYProgress.get() * (duration - restTime));

  useEffect(
    () => smoothed.on('change', (p) => setTime(restTime + p * (duration - restTime))),
    [smoothed, duration, restTime]
  );

  return time;
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/**
 * Contain-scales the fixed-size design canvas to whatever box the
 * parent renders, keeping the full composition visible (letterboxed, never
 * cropped) at any viewport size.
 */
function useContainScale(designWidth, designHeight) {
  const ref = useRef(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) {
        setScale(Math.min(width / designWidth, height / designHeight));
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [designWidth, designHeight]);

  return { ref, scale };
}

/** Confetti burst — same particle effect as the original hero's Shop Now button. */
const confettiBurst = (originEl) => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const rect = originEl.getBoundingClientRect();
  const cx = (rect.left + rect.width / 2) / window.innerWidth;
  const cy = (rect.top + rect.height / 2) / window.innerHeight;
  const colors = ['#3b82f6', '#60a5fa', '#93c5fd', '#ffffff', '#bfdbfe'];
  const container = document.createElement('canvas');
  container.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:9999';
  document.body.appendChild(container);
  const ctx = container.getContext('2d');
  container.width = window.innerWidth;
  container.height = window.innerHeight;
  const particles = Array.from({ length: 52 }, () => ({
    x: cx * container.width,
    y: cy * container.height,
    vx: (Math.random() - 0.5) * 14,
    vy: -(Math.random() * 12 + 4),
    r: Math.random() * 5 + 2,
    color: colors[Math.floor(Math.random() * colors.length)],
    life: 1,
    rot: Math.random() * Math.PI * 2,
    rotV: (Math.random() - 0.5) * 0.22,
  }));
  let raf;
  const tick = () => {
    ctx.clearRect(0, 0, container.width, container.height);
    let alive = false;
    for (const p of particles) {
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.35;
      p.vx *= 0.98;
      p.life -= 0.016;
      p.rot += p.rotV;
      if (p.life <= 0) continue;
      alive = true;
      ctx.save();
      ctx.globalAlpha = Math.max(0, p.life);
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.r, -p.r / 2, p.r * 2, p.r);
      ctx.restore();
    }
    if (alive) { raf = requestAnimationFrame(tick); }
    else { cancelAnimationFrame(raf); container.remove(); }
  };
  tick();
};

/**
 * Home page hero — a scroll-scrubbed cinematic explainer rather than an
 * autoplaying video. The story is pinned to the viewport while the user
 * scrolls through it; scroll position maps directly to the timeline, so
 * scrolling back up rewinds it. The CTAs live in normal document flow
 * right after the pinned section, so they appear naturally once scrolled
 * past — no separate "done" state to manage.
 *
 * Affordances: a scene rail (right edge) shows where you are in the story
 * and jumps straight to a scene on click; a "Skip story" control
 * fast-forwards past the pinned section; and with prefers-reduced-motion
 * the pin/scrub collapses to a single static hero frame.
 *
 * The canvas is purely decorative (an animated illustration of the
 * product story), so it's aria-hidden; a real <h1> + description carry
 * the same message for SEO and screen readers.
 */
const clamp01 = (v) => Math.max(0, Math.min(1, v));

/** Maps a timeline second to its scroll progress within the pinned section. */
const timeToProgress = (t) => clamp01((t - REST_TIME) / (EXPLAINER_DURATION - REST_TIME));

const ExplainerHero = () => {
  const scrollRef = useRef(null);
  const prefersReduced = useReducedMotion();
  const scrubbedTime = useScrollDrivenTime(scrollRef, EXPLAINER_DURATION, REST_TIME);
  // Reduced motion: no 400vh pin/scrub — just the settled intro frame as a
  // static hero, with the rest of the page continuing right below it.
  const time = prefersReduced ? REST_TIME : scrubbedTime;
  // Portrait viewports get the 9:16 recomposition of the story; landscape
  // gets the 16:9 stage. Same timeline, so the scrub position survives a
  // mid-story rotation.
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const designW = isPortrait ? 1080 : 1920;
  const designH = isPortrait ? 1920 : 1080;
  const { ref: canvasBoxRef, scale } = useContainScale(designW, designH);
  const shopBtnRef = useRef(null);

  // Fades out over the first ~1.2s of scrubbed time once scrolling begins.
  const hintOpacity = Math.max(0, 1 - Math.max(0, time - REST_TIME) / 1.2);
  // "Skip story" disappears once the outro is reached — nothing left to skip.
  const skipVisible = time < EXPLAINER_SCENES[EXPLAINER_SCENES.length - 1].t;

  let sceneIndex = 0;
  for (let i = 0; i < EXPLAINER_SCENES.length; i++) {
    if (time >= EXPLAINER_SCENES[i].t) sceneIndex = i;
  }

  const scrollToProgress = (p) => {
    const el = scrollRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY;
    const scrollable = el.offsetHeight - window.innerHeight;
    window.scrollTo({ top: Math.round(top + clamp01(p) * scrollable), behavior: 'smooth' });
  };

  return (
    <>
      <section
        ref={scrollRef}
        data-testid="hero-section"
        className="relative w-full bg-[#0a0a0a]"
        style={{ height: prefersReduced ? '100vh' : `${SCROLL_VH}vh` }}
      >
        <h1 className="sr-only">ClearWay — Smart Mobility. Real Confidence.</h1>
        <p className="sr-only">
          A fully integrated smart mobility stick that helps visually impaired and elderly
          users navigate the world safely and independently.
        </p>

        <div className="sticky top-0 h-screen w-full overflow-hidden pt-32 sm:pt-40">
          <div
            ref={canvasBoxRef}
            aria-hidden="true"
            className="relative w-full overflow-hidden mx-auto"
            style={{ aspectRatio: `${designW} / ${designH}`, maxHeight: 'calc(100vh - 8rem)' }}
          >
            <div
              style={{
                position: 'absolute', left: '50%', top: '50%', width: designW, height: designH,
                transform: `translate(-50%, -50%) scale(${scale})`, transformOrigin: 'center',
              }}
            >
              {isPortrait ? <ClearWayExplainerMobile time={time} /> : <ClearWayExplainer time={time} />}
            </div>
          </div>

          {/* Scroll hint — fades out the moment the user starts scrubbing */}
          {!prefersReduced && (
            <div
              aria-hidden="true"
              className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1"
              style={{ opacity: hintOpacity, transition: 'opacity 0.3s ease' }}
            >
              <span className="text-gray-500 text-xs font-medium tracking-widest uppercase">Scroll to explore</span>
              <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}>
                <ChevronDown className="w-5 h-5 text-gray-600" />
              </motion.div>
            </div>
          )}

          {/* Scene progress rail — shows where you are in the story and jumps
              to a scene on click (scroll position is the timeline, so a jump
              is just a smooth scrollTo). */}
          {!prefersReduced && (
            <nav
              aria-label="Story progress"
              className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 flex flex-col items-center"
            >
              {EXPLAINER_SCENES.map((s, i) => (
                <button
                  key={s.label}
                  type="button"
                  title={s.label}
                  aria-label={`Go to scene: ${s.label}`}
                  aria-current={i === sceneIndex ? 'step' : undefined}
                  onClick={() => scrollToProgress(timeToProgress(s.t + 0.8))}
                  className="group p-1.5 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/70"
                >
                  <span
                    className={`block w-2 rounded-full transition-all duration-300 ${
                      i === sceneIndex ? 'h-5 bg-blue-400' : 'h-2 bg-white/25 group-hover:bg-white/60'
                    }`}
                  />
                </button>
              ))}
            </nav>
          )}

          {/* Escape hatch for anyone who doesn't want the story */}
          {!prefersReduced && (
            <button
              type="button"
              data-testid="hero-skip-story"
              onClick={() => scrollToProgress(1)}
              className="absolute bottom-8 right-3 sm:right-6 px-4 py-2 rounded-full border border-white/15 text-gray-500 hover:text-white hover:border-white/40 text-[11px] font-medium tracking-widest uppercase focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/70"
              style={{
                opacity: skipVisible ? 1 : 0,
                pointerEvents: skipVisible ? 'auto' : 'none',
                transition: 'opacity 0.3s ease, color 0.15s ease, border-color 0.15s ease',
              }}
            >
              Skip story
            </button>
          )}
        </div>
      </section>

      {/* CTAs — normal document flow right after the pinned story */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-40px' }}
        transition={{ duration: 0.6, ease: [0.25, 0, 0, 1] }}
        className="flex flex-col sm:flex-row items-center justify-center gap-4 px-6 py-16 sm:py-20 bg-[#0a0a0a]"
      >
        <MagneticButton>
          <motion.div ref={shopBtnRef} whileTap={{ scale: 0.96 }}>
            <Link
              to="/shop"
              data-testid="hero-shop-now"
              onClick={() => shopBtnRef.current && confettiBurst(shopBtnRef.current)}
              className="group px-8 py-4 rounded-full bg-white text-black font-medium flex items-center gap-2 hover:bg-gray-100 transition-colors"
            >
              Shop Now
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </motion.div>
        </MagneticButton>
        <MagneticButton>
          <motion.div whileTap={{ scale: 0.96 }}>
            <Link
              to="/features"
              data-testid="hero-explore"
              className="px-8 py-4 rounded-full border border-white/20 text-white font-medium hover:bg-white/[0.06] hover:border-white/30 transition-colors"
            >
              Explore ClearWay
            </Link>
          </motion.div>
        </MagneticButton>
        <MagneticButton>
          <motion.div whileTap={{ scale: 0.96 }}>
            <Link
              to="/partner"
              data-testid="hero-partner"
              className="px-8 py-4 rounded-full border border-white/20 text-white font-medium hover:bg-white/[0.06] hover:border-white/30 transition-colors"
            >
              Partner With Us
            </Link>
          </motion.div>
        </MagneticButton>
      </motion.div>
    </>
  );
};

export default ExplainerHero;
